// Props:
//  resizeTrigger: flip when containing window has been resized, request a map redraw
//
//
// mapConfig = {
//   latN:
//   latS:
//   lngW:
//   lngE:
//   label: Text to display in attribution control
// }

// mapCenter = [latitude, longitude]

// polygons = [polygon]

// markersPending: set to false right after markersArrayRef refreshes, triggers a display refresh
//
// markersArrayRef = {
//   current: {
//     id: PxUid,
//     type: icon type,
//     code: icon code name,
//     anchor: relative anchor location { x: 0 to 1, y: 0 to 1}
//     angle: optional, marker tilt in degrees
//     opacity: optional
//     sizePx: max. marker display width/height
//     lat:
//     lng:
//   }
// }
//
// mapEvent: callback function, can send:
//
// {event: 'clickMap'}
// {event: 'click', type: marker.type, code: marker.code,
//    id: marker.id, coordinates: containerPoint}
// {event: 'hover', type: marker.type, code: marker.code,
//    id: marker.id, coordinates: containerPoint}
// {event: 'leave', type: marker.type, code: marker.code, id: marker.id}
// {event: 'updateBounds', newBounds}
//
// Where newBounds = [[SWlat, SWlng], [NElat, NElng]]
// and containerPoint = [windowXPixels, windowYPixels]

import PropTypes from 'prop-types';
import {
  useEffect,
  useRef,
  useReducer,
  useCallback,
  useMemo,
  useState,
} from 'react';

import {
  emptyArray,
  emptyFunction,
  emptyObject,
} from '../../utils';
import worldPolygon from '../../utils/geo/worldPolygon';
import {
  LeafletAttributionControl,
  LeafletCircle,
  LeafletMap,
  LeafletPolygon,
  LeafletTileLayer,
} from '../leaflet';

import PxMapActions from './PxMapActions';
import PxMapControls from './PxMapControls';
import PxMapMarkersOverlay from './PxMapMarkersOverlay';

const minZoom = 3;
const maxZoom = 19;

const mapInitialState = {
  mapReady: false,
  markersContainerReady: false,
  allReady: false,
};

const mapStateReducer = (state, action) => {
  // console.log(`Map state action=${JSON.stringify(action)}, state=${JSON.stringify(state)}`);
  switch (action.type) {
    case 'setMapReady':
      if (state.mapReady === action.ready) return state;
      return ({
        ...state,
        mapReady: action.ready,
        allReady: action.ready && state.markersContainerReady,
      });

    case 'setMarkersContainerReady':
      if (state.markersContainerReady === action.ready) return state;
      return ({
        ...state,
        markersContainerReady: action.ready,
        allReady: action.ready && state.mapReady,
      });

    default:
      throw new Error();
  }
};

const emptyArrayRef = {
  current: [],
};

function PxMap(props) {
  const {
    children = null,
    style = emptyObject,
    className,
    resizeTrigger,
    mapConfig,
    mapCenter,
    markersPending,
    markersArrayRef = emptyArrayRef,
    polygons = emptyArray,
    mapEvent = emptyFunction,
    hideControls,
    hideAttribution,
    attributionPosition,
    initialZoom = 12,
    circles = emptyArray,
    crosshairMode,
    zoomAction,
    withWorldPolygon = true,
    mobile,
    customButtons,
  } = props;
  const {
    latN = 80,
    latS = -80,
    lngW = -180,
    lngE = 180,
  } = mapConfig;
  // Persistent local variables, which don't directly trigger a React redraw:
  const mapZoom = useRef(initialZoom);

  const [mapState, mapStateDispatch] = useReducer(mapStateReducer, mapInitialState);

  useState(
    () => {
      if (mobile) {
        mapEvent({ event: 'log', txt: 'Map created' });
        return () => {
          mapEvent({
            event: 'log',
            txt: 'Map closed',
          });
        };
      }
      return undefined;
    },
    [],
  );

  // useState(
  //   () => {
  //     console.log(`Map state=${JSON.stringify(mapState)}`);
  //     if (mapState.allReady) {
  //       mapEvent({ event: 'mapAllReady' });
  //     }
  //     return undefined;
  //   },
  //   [mapState, mapState.ready, mapState.allReady, mapState.markersContainerReady],
  // );

  const calcMapCenter = useMemo(
    () => (mapCenter
          || [(latN + latS) / 2, (lngW + lngE) / 2]),
    [mapCenter, latN, latS, lngE, lngW],
  );

  const polygonsPositions = useMemo(
    () => {
      if (polygons.length) {
        if (withWorldPolygon) return [worldPolygon, ...polygons];

        return polygons;
      }
      return null;
    },
    [polygons, withWorldPolygon],
  );

  const setZoom = useCallback(
    (newZoom) => {
      mapZoom.current = newZoom;
    },
    [],
  );

  // useEffect(() => {
  //   const webGlSupported = PIXI.utils.isWebGLSupported();
  //   mapEvent({
  //     event: 'log',
  //     txt: `%c !!!!!!!!!!!!!!!!!!!!!!\n pixiUtils.isWebGLSupported=${webGlSupported}`,
  //     options: (webGlSupported) ? 'color: green' : 'color: red',
  //   });
  // },
  // [mapEvent]);

  useEffect(
    () => {
      if ((zoomAction === '+') && (mapZoom.current < maxZoom)) {
        mapZoom.current += 1;
      }
      if ((zoomAction === '-') && (mapZoom.current > minZoom)) {
        mapZoom.current -= 1;
      }
    },
    [zoomAction],
  );

  // const overlayClick = useCallback(
  //   () => {
  //     console.log('mob overlay click event');
  //   },
  //   [],
  // );

  const whenReady = useCallback(
    () => {
      // console.log('PxMap whenReady');
      mapStateDispatch({ type: 'setMapReady', ready: true });
      if (mapEvent) mapEvent({ event: 'mapReady' });
    },
    [mapEvent],
  );

  return (
    <>
      <LeafletMap
        preferCanvas
        style={{
          width: '100%',
          height: '100%',
          // https://stackoverflow.com/questions/14106687/how-do-i-change-the-default-cursor-in-leaflet-maps
          cursor: (crosshairMode) ? 'crosshair' : undefined,
          ...style,
        }}
        className={className}
        center={calcMapCenter}
        zoom={mapZoom.current}
        // zoom={13}
        minZoom={minZoom}
        maxZoom={maxZoom}
        zoomControl={false}
        attributionControl={false}
        whenReady={whenReady}
      >

        {(!hideAttribution)
          ? (
              <LeafletAttributionControl
                position={attributionPosition || 'topleft'}
                prefix={(mapConfig.label)
                  ? `${mapConfig.label} - Paxity`
                  : ''}
              />
            )
          : null}

        <LeafletTileLayer
        // attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
          attribution={(mapConfig.label)
            ? '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            : '&copy; <a href="http://osm.org/copyright">OSM.org</a>'}
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png?"
          maxZoom={maxZoom}
          // Beyond maxNativeZoom, image is just scaled (not recomputed)
          // > depends on layer details level
          // maxNativeZoom={19}
        />

        {
          (!hideControls)
            ? <PxMapControls customButtons={customButtons} />
            : null
        }

        {
          (polygonsPositions?.length)
            ? (
                <LeafletPolygon
                  positions={polygonsPositions}
                  pathOptions={{
                    // edge:
                    color: '#3377e0',
                    opacity: 1,
                    weight: 4,
                    dashArray: '8, 10',
                    // body:
                    fillColor: '#bbccdd',
                    fillOpacity: '0.3',
                    // options:
                    interactive: false,
                  }}
                />
              )
            : null
        }

        {
          // Used to display zone limits etc...
          circles.map((thisCircle, index) => (
            <LeafletCircle
              key={[index]}
              center={[thisCircle.lat, thisCircle.lng]}
              radius={thisCircle.radius}
              pathOptions={{
                color: thisCircle.color,
                opacity: thisCircle.opacity,
                stroke: false,
              }}
            />
          ))
        }

        <PxMapActions
          allReady={mapState.allReady}
          mapConfig={mapConfig}
          mapEvent={mapEvent}
          resizeTrigger={resizeTrigger}
          setZoom={setZoom}
          calcMapCenter={calcMapCenter}
        />

        <PxMapMarkersOverlay
          allReady={mapState.allReady}
          mapStateDispatch={mapStateDispatch}
          mapEvent={mapEvent}
          markersPending={markersPending}
          markersArrayRef={markersArrayRef}
        />

        {children}

      </LeafletMap>

      {/* To bring back, remove during leaflet update */}

      {/* {(crosshairMode && mobile)
        ? (
          // This is needed on Android, where tap's on the leaflet div do
          //  not generate click events
          //
          // eslint-disable-next-line max-len
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,
          jsx-a11y/no-static-element-interactions
          <div
            id="mapTouchOverlay"
            className="mapClickTouchOverlay"
            onTouchStart={touchOverlay}
            onClick={clickOverlay}
          />
        )
        : null} */}

    </>
  );
}

PxMap.propTypes = {
  children: PropTypes.node,
  style: PropTypes.shape({ }),
  className: PropTypes.string,
  resizeTrigger: PropTypes.bool,
  mapConfig: PropTypes.shape({
    latN: PropTypes.number,
    latS: PropTypes.number,
    lngW: PropTypes.number,
    lngE: PropTypes.number,
    label: PropTypes.string,
  }).isRequired,
  mapCenter: PropTypes.arrayOf(
    PropTypes.number,
  ),
  polygons: PropTypes.arrayOf(PropTypes.shape),
  circles: PropTypes.arrayOf(PropTypes.shape({
    lng: PropTypes.number,
    lat: PropTypes.number,
    radius: PropTypes.number,
    color: PropTypes.string,
    opacity: PropTypes.number,
  })),
  markersPending: PropTypes.bool.isRequired,
  markersArrayRef: (PropTypes.shape({
    current: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        type: PropTypes.string,
        code: PropTypes.string,
        lat: PropTypes.number,
        lng: PropTypes.number,
        anchor: PropTypes.shape({
          x: PropTypes.number,
          y: PropTypes.number,
        }),
        angle: PropTypes.number,
        opacity: PropTypes.number,
      }),
    ),
  })),
  mapEvent: PropTypes.func,
  hideControls: PropTypes.bool,
  hideAttribution: PropTypes.bool,
  attributionPosition: PropTypes.string,
  initialZoom: PropTypes.number,
  crosshairMode: PropTypes.bool,
  zoomAction: PropTypes.string,
  withWorldPolygon: PropTypes.bool,
  mobile: PropTypes.bool,
  customButtons: PropTypes.arrayOf(PropTypes.shape({
    imgSrc: PropTypes.string.isRequired,
    onClick: PropTypes.func,
  })),
};

export default PxMap;
